import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import FooterColumn from "@/components/ui/footer-column";
import BrandAnalyzerForm from "@/components/BrandAnalyzerForm";
import AnimatedShaderBackground from "@/components/ui/animated-shader-background";
import { Globe, Building2, Users, MessageSquare, Target } from "lucide-react";
import SEO from "@/components/SEO";
const BrandAnalyzerPage = () => {
  return <div className="min-h-screen bg-background relative">
      <SEO data={{
      title: "Free Brand Analysis & Content Strategy | gimmefy",
      description: "Get a free, AI-powered brand analysis report covering business profile, tone of voice, audience insights, competitive landscape, and more. Includes 1000 free credits and demo account.",
      keywords: "free brand analysis, brand analyzer, AI brand report, content strategy, brand audit, competitive analysis, SWOT analysis, brand assessment",
      imageUrl: "/lovable-uploads/hero-ai-marketing.jpg"
    }} canonicalUrl="https://gimmefy.ai/360-brand-ai-deep-dive" />
      
      {/* Fixed Background - Outside of scroll container */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <AnimatedShaderBackground />
      </div>
      <div className="fixed inset-0 bg-background/40 z-10 pointer-events-none" />
      
      {/* Scrollable Content Container */}
      <div className="relative z-20 overflow-x-hidden pb-4">
        <Navigation />
        
        {/* Hero Section */}
        <section className="relative z-20 py-12 md:py-16">
          <div className="container mx-auto px-4">
          <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">Get a 360° AI Brand Report—On Us</h1>
            <p className="text-base md:text-lg font-semibold text-foreground/90 mb-3">Built on our USD 3,000 Brand Vault framework—your exclusive report is yours for free.</p>
            <p className="text-lg md:text-xl text-muted-foreground mb-4">See your brand from a whole new vantage point—discover what AI and your customers are really seeing.
You’ll get a clear, candid summary of everything you know—or thought you knew—about your brand, plus fresh insights to help you show up even stronger.</p>
          </motion.div>
          </div>
        </section>

        {/* Single Column Section: Horizontal Row + 2-Column Layout */}
        <section className="relative z-20 py-8 md:py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }}>
              <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">What you'll get as a part of your free report</h2>
            </motion.div>

            {/* Responsive Grid - First 5 Benefits */}
            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-6 lg:grid-cols-5 gap-4 [&>*:nth-child(n+1):nth-child(-n+3)]:md:max-lg:col-span-2 [&>*:nth-child(n+4)]:md:max-lg:col-span-3">
                {[{
                  icon: Building2,
                  title: "Business Profile",
                  description: "Your brand at a glance—USPs, mission, and ideal audience, clearly defined.",
                  gradient: "from-blue-500/20 to-cyan-500/20",
                  iconBg: "bg-gradient-to-br from-blue-500 to-cyan-500",
                  iconColor: "text-white"
                }, {
                  icon: MessageSquare,
                  title: "Tone of Voice",
                  description: "How your brand sounds—keeping every message unmistakably yours.",
                  gradient: "from-purple-500/20 to-pink-500/20",
                  iconBg: "bg-gradient-to-br from-purple-500 to-pink-500",
                  iconColor: "text-white"
                }, {
                  icon: Users,
                  title: "Audience Profile",
                  description: "Spot untapped audiences and build targeted strategies in minutes.",
                  gradient: "from-orange-500/20 to-red-500/20",
                  iconBg: "bg-gradient-to-br from-orange-500 to-red-500",
                  iconColor: "text-white"
                }, {
                  icon: Target,
                  title: "Customer Analysis",
                  description: "Understand and turn deep customer insights into action with gimmefy.",
                  gradient: "from-green-500/20 to-emerald-500/20",
                  iconBg: "bg-gradient-to-br from-green-500 to-emerald-500",
                  iconColor: "text-white"
                }, {
                  icon: Globe,
                  title: "Competitive Landscape",
                  description: "See where you stand and outsmart your rivals for a winning edge.",
                  gradient: "from-indigo-500/20 to-violet-500/20",
                  iconBg: "bg-gradient-to-br from-indigo-500 to-violet-500",
                  iconColor: "text-white"
                }].map((item, index) => <motion.div key={index} initial={{
                  opacity: 0,
                  y: 20
                }} whileInView={{
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.5,
                  delay: index * 0.1
                }} className={`relative overflow-hidden rounded-xl p-5 bg-gradient-to-br ${item.gradient} border border-border/50 backdrop-blur-sm hover:scale-[1.02] transition-all duration-300`}>
                    <div className="flex flex-col gap-4">
                      <div className="flex-shrink-0">
                        <div className={`w-14 h-14 rounded-xl ${item.iconBg} flex items-center justify-center shadow-lg`}>
                          <item.icon className={`w-7 h-7 ${item.iconColor}`} />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-base mb-2 text-foreground">{item.title}</h3>
                        <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>

            <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }}>
              <BrandAnalyzerForm />
            </motion.div>
          </div>
        </div>
      </section>

      <FooterColumn />
      </div>
    </div>;
};
export default BrandAnalyzerPage;